// DocumentationComplianceReport.jsx - Track plans without authorizations
// Hierarchy: Month > Consumer > Plan
import React, { useState, useEffect, useContext, useCallback } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { auditApi } from "../../utils/api";
import secureAxios from "../../utils/secureAxios";
import { toast } from "react-toastify";

const DocumentationComplianceReport = () => {
  const { user } = useContext(AuthContext);
  const tenant_id = user?.tenant_id;

  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filters, setFilters] = useState({
    customer_id: "",
    start_date: "",
    end_date: "",
  });
  const [customers, setCustomers] = useState([]);

  // Expansion state for each level
  const [expandedMonths, setExpandedMonths] = useState({});
  const [expandedConsumers, setExpandedConsumers] = useState({});

  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const customerRes = await secureAxios.get("/api/customers", {
          params: { tenant_id },
        });
        setCustomers(customerRes.data || []);
      } catch (err) {
        console.error("Error loading filter options:", err);
      }
    };
    if (tenant_id) loadFilterOptions();
  }, [tenant_id]);

  const loadReport = useCallback(async () => {
    if (!tenant_id) return;
    setLoading(true);
    try {
      const response = await secureAxios.get(
        "/api/business-reports/documentation-compliance",
        {
          params: {
            tenant_id,
            customer_id: filters.customer_id || undefined,
            start_date: filters.start_date || undefined,
            end_date: filters.end_date || undefined,
          },
        }
      );

      if (response.data.success) {
        setReportData(response.data.data || []);
        setSummary(response.data.summary || null);

        await auditApi.logEntry({
          action: "GENERATE_DOC_COMPLIANCE_REPORT",
          entity_type: "REPORT",
          entity_id: null,
          contains_phi: true,
          action_status: "SUCCESS",
          purpose_of_use: "OPERATIONS",
        });
      }
    } catch (err) {
      console.error("Error loading documentation compliance:", err);
      toast.error("Failed to load documentation compliance report");
    } finally {
      setLoading(false);
    }
  }, [tenant_id, filters]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  // Toggle functions
  const toggleMonth = (monthKey) => {
    setExpandedMonths((prev) => ({
      ...prev,
      [monthKey]: !prev[monthKey],
    }));
  };

  const toggleConsumer = (monthKey, consumerId) => {
    const key = `${monthKey}-${consumerId}`;
    setExpandedConsumers((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const expandAll = () => {
    const allMonths = {};
    const allConsumers = {};
    reportData.forEach((month) => {
      allMonths[month.month_key] = true;
      month.consumers?.forEach((consumer) => {
        allConsumers[`${month.month_key}-${consumer.consumer_id}`] = true;
      });
    });
    setExpandedMonths(allMonths);
    setExpandedConsumers(allConsumers);
  };

  const collapseAll = () => {
    setExpandedMonths({});
    setExpandedConsumers({});
  };

  // Count total consumers and plans
  const totalConsumers = reportData.reduce(
    (acc, month) => acc + (month.consumers?.length || 0),
    0
  );

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">
            <i className="bi bi-file-earmark-check me-2"></i>
            Documentation Compliance
          </h2>
          <p className="text-muted mb-0">
            Identify plans without authorizations
          </p>
        </div>
        <button
          className="btn btn-outline-primary"
          onClick={loadReport}
          disabled={loading}
        >
          <i className="bi bi-arrow-clockwise me-1"></i>
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-3">
              <label className="form-label">Customer</label>
              <select
                className="form-select"
                value={filters.customer_id}
                onChange={(e) =>
                  setFilters({ ...filters, customer_id: e.target.value })
                }
              >
                <option value="">All Customers</option>
                {customers.map((c) => (
                  <option key={c.customer_id} value={c.customer_id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Start Date</label>
              <input
                type="date"
                className="form-control"
                value={filters.start_date}
                onChange={(e) =>
                  setFilters({ ...filters, start_date: e.target.value })
                }
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">End Date</label>
              <input
                type="date"
                className="form-control"
                value={filters.end_date}
                onChange={(e) =>
                  setFilters({ ...filters, end_date: e.target.value })
                }
              />
            </div>
            <div className="col-md-3">
              <button
                className="btn btn-outline-secondary w-100"
                onClick={() =>
                  setFilters({ customer_id: "", start_date: "", end_date: "" })
                }
              >
                <i className="bi bi-x-circle me-1"></i>
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="row g-3 mb-4">
          <div className="col-6 col-md-3">
            <div className="card bg-warning text-dark h-100">
              <div className="card-body text-center py-3">
                <h6 className="card-subtitle mb-1 opacity-75 small">
                  Plans Without Auth
                </h6>
                <h3 className="card-title mb-0">
                  {summary.plans_without_auth || 0}
                </h3>
              </div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="card bg-dark text-white h-100">
              <div className="card-body text-center py-3">
                <h6 className="card-subtitle mb-1 opacity-75 small">
                  Expired Auths
                </h6>
                <h3 className="card-title mb-0">
                  {summary.expired_auths || 0}
                </h3>
              </div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="card bg-info text-white h-100">
              <div className="card-body text-center py-3">
                <h6 className="card-subtitle mb-1 opacity-75 small">
                  Consumers Affected
                </h6>
                <h3 className="card-title mb-0">{totalConsumers}</h3>
              </div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="card bg-primary text-white h-100">
              <div className="card-body text-center py-3">
                <h6 className="card-subtitle mb-1 opacity-75 small">
                  Total Issues
                </h6>
                <h3 className="card-title mb-0">{summary.total_issues || 0}</h3>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      {/* Hierarchical Data - Month > Consumer > Plan */}
      {!loading && reportData.length > 0 && (
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <span className="fw-bold">
              <i className="bi bi-calendar3 me-2"></i>
              {reportData.length} Month{reportData.length !== 1 ? "s" : ""} with
              Issues
            </span>
            <div>
              <button
                className="btn btn-sm btn-outline-secondary me-2"
                onClick={expandAll}
              >
                <i className="bi bi-arrows-expand me-1"></i>
                Expand All
              </button>
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={collapseAll}
              >
                <i className="bi bi-arrows-collapse me-1"></i>
                Collapse All
              </button>
            </div>
          </div>
          <div className="card-body p-0">
            {reportData.map((month) => {
              const isMonthExpanded = expandedMonths[month.month_key];

              return (
                <div key={month.month_key} className="border-bottom">
                  {/* Month Header */}
                  <div
                    className="d-flex align-items-center p-3"
                    style={{ cursor: "pointer", backgroundColor: "#e3f2fd" }}
                    onClick={() => toggleMonth(month.month_key)}
                  >
                    <i
                      className={`bi ${
                        isMonthExpanded ? "bi-chevron-down" : "bi-chevron-right"
                      } me-2`}
                    ></i>
                    <i className="bi bi-calendar-month me-2 text-primary"></i>
                    <div className="flex-grow-1">
                      <span className="fw-bold">{month.month_label}</span>
                    </div>
                    <span className="badge bg-warning text-dark me-2">
                      {month.plan_count} plan{month.plan_count !== 1 ? "s" : ""}
                    </span>
                    <span className="badge bg-secondary">
                      {month.consumers?.length || 0} consumer
                      {(month.consumers?.length || 0) !== 1 ? "s" : ""}
                    </span>
                  </div>

                  {/* Consumers (expanded) */}
                  {isMonthExpanded &&
                    month.consumers?.map((consumer) => {
                      const consumerKey = `${month.month_key}-${consumer.consumer_id}`;
                      const isConsumerExpanded = expandedConsumers[consumerKey];

                      return (
                        <div key={consumer.consumer_id} className="border-top">
                          {/* Consumer Header */}
                          <div
                            className="d-flex align-items-center p-3 ps-4 bg-light"
                            style={{ cursor: "pointer" }}
                            onClick={() =>
                              toggleConsumer(
                                month.month_key,
                                consumer.consumer_id
                              )
                            }
                          >
                            <i
                              className={`bi ${
                                isConsumerExpanded
                                  ? "bi-chevron-down"
                                  : "bi-chevron-right"
                              } me-2`}
                            ></i>
                            <i className="bi bi-person-fill me-2 text-success"></i>
                            <div className="flex-grow-1">
                              <span className="fw-medium">
                                {consumer.consumer_name}
                              </span>
                              <span className="text-muted ms-2 small">
                                ({consumer.customer_name})
                              </span>
                            </div>
                            <span className="badge bg-warning text-dark">
                              {consumer.plan_count} plan
                              {consumer.plan_count !== 1 ? "s" : ""}
                            </span>
                          </div>

                          {/* Plans (expanded) */}
                          {isConsumerExpanded &&
                            consumer.plans?.map((plan, idx) => (
                              <div
                                key={plan.plan_id || idx}
                                className="d-flex align-items-center p-3 ps-5 border-top bg-white"
                              >
                                <i className="bi bi-file-earmark-text me-3 text-muted"></i>
                                <div className="flex-grow-1">
                                  <div className="fw-medium">
                                    {plan.plan_name}
                                  </div>
                                  <div className="small text-muted">
                                    Plan Date: {plan.plan_date_formatted}
                                  </div>
                                </div>
                                <span className="badge bg-warning text-dark me-2">
                                  <i className="bi bi-exclamation-triangle me-1"></i>
                                  No Authorization
                                </span>
                              </div>
                            ))}

                          {/* Action footer for consumer */}
                          {isConsumerExpanded && (
                            <div className="p-2 ps-5 bg-light border-top small text-muted">
                              <i className="bi bi-arrow-right me-1"></i>
                              <strong>Action:</strong> Request authorization
                              from counselor
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* No Data */}
      {!loading && reportData.length === 0 && (
        <div className="text-center py-5">
          <i
            className="bi bi-check-circle text-success"
            style={{ fontSize: "3rem" }}
          ></i>
          <h5 className="mt-3">No Compliance Issues Found</h5>
          <p className="text-muted">All plans have authorizations created!</p>
        </div>
      )}
    </div>
  );
};

export default DocumentationComplianceReport;
